import React, { useEffect, useState } from "react";
import { MapPin, Plus, ChevronRight, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@apollo/client/react";

import { CustomHeader } from "../components/AuthHeader";
import BagBarcodeInput from "../components/BarCodeReader";
import { CustomButton } from "../components/CustomButton";
import { TimeSlotPicker, SlotSelection } from "../components/TimeSlotPicker";
import { useToast } from "../components/ToastProvider";

import { useOrderStore } from "../features/orders/orderStore";
import { PickType } from "../features/orders/types";

import {
  GET_LOCKER_LOCATIONS,
  GET_CUSTOMER_LOCATIONS,
} from "../features/location/locationQueries";

import rushLogo from "../assets/png/rush-logo.png";

// --- Interfaces for TypeScript Safety ---
interface CustomerLocation {
  id: string;
  location: string;
  label: string;
  city?: string;
}

interface CustomerLocationsData {
  customerLocations: CustomerLocation[];
}

interface LockerLocation {
  id: string;
  location: string;
  is_active: boolean;
}

interface LockerLocationsData {
  lockerLocations: LockerLocation[];
}

const NOTE_SUGGESTIONS = ["Press only", "Delicate", "No starch", "Hang dry"];

// --- Utilities ---
const buildLocationText = (loc: CustomerLocation) => {
  if (!loc) return "";
  return `${loc.location || ""}${loc.city ? `, ${loc.city}` : ""}`;
};

/**
 * Customer side of the order: bag number, locker or home, location, pickup
 * slot (home) and instructions. Staff list the items and prices once the bag
 * reaches the shop, and the customer then reviews and pays from the order page.
 */
export default function NewOrderFlow() {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const { isLoading: isOrderLoading, createOrder } = useOrderStore();

  // --- Data Fetching (Typed Hooks) ---
  const { data: locationData } = useQuery<CustomerLocationsData>(
    GET_CUSTOMER_LOCATIONS,
    {
      fetchPolicy: "cache-and-network",
    },
  );
  const locations = locationData?.customerLocations ?? [];

  const {
    data: lockerData,
    loading: lockerLoading,
    error: lockerError,
  } = useQuery<LockerLocationsData>(GET_LOCKER_LOCATIONS, {
    fetchPolicy: "cache-and-network",
  });
  const lockerLocations = lockerData?.lockerLocations ?? [];

  // --- Local Component State ---
  const [bagNo, setBagNo] = useState("");
  const [pickupType, setPickupType] = useState<PickType>("locker");
  const [selectedLocationId, setSelectedLocationId] = useState("");
  const [selectedLockerId, setSelectedLockerId] = useState("");
  const [pickupSlot, setPickupSlot] = useState<SlotSelection>(null);
  const [note, setNote] = useState("");
  const [detailErrors, setDetailErrors] = useState<Record<string, string>>({});

  // --- Effects ---
  useEffect(() => {
    if (locations.length > 0 && !selectedLocationId) {
      setSelectedLocationId(locations[0].id);
    }
  }, [locations, selectedLocationId]);

  useEffect(() => {
    if (lockerError) showToast("Failed to load locker locations", "error");
  }, [lockerError, showToast]);

  // --- Derived ---
  const selectedSavedLocation = locations.find(
    (l: any) => l.id === selectedLocationId,
  );
  const selectedLocker = lockerLocations.find(
    (l: any) => l.id === selectedLockerId,
  );

  const locationText =
    pickupType === "home"
      ? selectedSavedLocation
        ? buildLocationText(selectedSavedLocation)
        : ""
      : selectedLocker
        ? selectedLocker.location
        : "";

  // --- Handlers ---
  const addNoteSuggestion = (text: string) => {
    setNote((current) => {
      if (current.toLowerCase().includes(text.toLowerCase())) return current;
      return current.trim() ? `${current.trim()}, ${text}` : text;
    });
  };

  const handleConfirmOrder = async () => {
    const errors: Record<string, string> = {};
    if (!bagNo.trim()) errors.bagNo = "Please enter bag number.";
    if (pickupType === "home" && !selectedLocationId)
      errors.location = "Please select an address.";
    if (pickupType === "home" && !pickupSlot)
      errors.slot = "Please choose a pickup time.";
    if (pickupType === "locker" && !selectedLockerId)
      errors.locker = "Please select a locker.";

    setDetailErrors(errors);
    if (Object.keys(errors).length > 0) return;

    try {
      const createdOrder = await createOrder({
        pickupType,
        bagNo: bagNo.trim(),
        location: locationText,
        note: note.trim() || null,
        pickupSlotId: pickupType === "home" ? pickupSlot?.slotId : null,
        pickupSlotDate: pickupType === "home" ? pickupSlot?.date : null,
      });

      navigate("/order/success", {
        state: {
          id: createdOrder.id,
          orderId: createdOrder.order_no || createdOrder.id,
          pickupType,
          pickupSlot: createdOrder.pickup_slot,
          pickupSlotDate: createdOrder.pickup_slot_date,
        },
      });
    } catch (e) {
      showToast(
        e instanceof Error ? e.message : "Order creation failed. Please try again.",
        "error",
      );
    }
  };

  const Header = ({ onBack }: { onBack: () => void }) => (
    <div className="md:hidden sticky top-0 z-50 h-[76px] bg-white/90 backdrop-blur-md px-6 pt-4 pb-4 flex items-center justify-between">
      <button
        onClick={onBack}
        className="w-11 h-11 border border-gray-100 rounded-2xl flex items-center justify-center bg-white shadow-sm"
      >
        <ArrowLeft size={20} className="text-gray-700" />
      </button>
      <img src={rushLogo} alt="Rush" className="h-7 w-auto object-contain" />
    </div>
  );

  const FieldError = ({ field }: { field: string }) =>
    detailErrors[field] ? (
      <p className="text-xs text-red-500 mt-1.5 flex items-center gap-1">
        <span className="w-1 h-1 rounded-full bg-red-500" />{" "}
        {detailErrors[field]}
      </p>
    ) : null;

  return (
    <div className="min-h-screen md:min-h-0 bg-gray-50 md:bg-transparent flex flex-col pb-6 md:max-w-2xl md:mx-auto md:w-full">
      <Header onBack={() => navigate("/home")} />
      <div className="bg-white px-6 pb-6 md:pt-6 shadow-sm rounded-b-[32px] md:rounded-3xl mb-6">
        <CustomHeader
          title="Place a New Order"
          subtitle="Tell us where your bag is. We'll list the items and send you the total."
        />
      </div>
      <div className="px-5 md:px-0 space-y-5">
          <BagBarcodeInput
            value={bagNo}
            onChange={setBagNo}
            onScan={setBagNo}
          />
          <FieldError field="bagNo" />

          <div>
            <p className="text-sm font-semibold text-gray-700 mb-2">
              Pickup & Drop
            </p>
            <div className="grid grid-cols-2 gap-3">
              {(["locker", "home"] as const).map((t) => (
                <button
                  key={t}
                  onClick={() => setPickupType(t)}
                  className={`rounded-2xl py-4 font-semibold text-sm transition-all ${pickupType === t ? "bg-[#44B6CE] text-white shadow-md" : "bg-white border border-gray-100 text-gray-600"}`}
                >
                  {t === "home" ? "🏠 Home" : "📦 Locker"}
                </button>
              ))}
            </div>
          </div>

          {pickupType === "home" ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-gray-700">
                  Select Address
                </p>
                <button
                  onClick={() => navigate("/profile/locations/add")}
                  className="text-xs font-semibold text-[#44B6CE] flex items-center gap-1"
                >
                  <Plus size={14} /> Add New
                </button>
              </div>
              <div className="relative">
                <select
                  value={selectedLocationId}
                  onChange={(e) => setSelectedLocationId(e.target.value)}
                  className="w-full appearance-none bg-white border border-[#44B6CE] shadow-sm rounded-2xl px-4 py-3 text-sm text-gray-800 outline-none"
                >
                  {locations.map((loc: any) => (
                    <option key={loc.id} value={loc.id}>
                      {loc.location.split(" - ")[0]}
                    </option>
                  ))}
                </select>
                <ChevronRight
                  size={18}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#44B6CE] rotate-90 pointer-events-none"
                />
              </div>
              {locations.length === 0 ? (
                <button
                  type="button"
                  onClick={() => navigate("/profile/locations/add")}
                  className="w-full bg-white border-2 border-dashed border-[#44B6CE]/30 rounded-2xl px-4 py-5 flex items-center gap-4 transition-all active:border-[#44B6CE]/60"
                >
                  <div className="w-10 h-10 rounded-xl bg-[#44B6CE]/10 flex items-center justify-center flex-shrink-0">
                    <MapPin size={18} className="text-[#44B6CE]" />
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-bold text-gray-800">
                      No saved addresses
                    </p>
                    <p className="text-xs text-[#44B6CE] font-semibold mt-0.5">
                      + Tap to add your address
                    </p>
                  </div>
                </button>
              ) : selectedSavedLocation ? (
                <div className="bg-white rounded-2xl border border-gray-100 p-3 flex items-center gap-3 shadow-sm">
                  <div className="w-10 h-10 rounded-xl bg-[#44B6CE]/10 flex items-center justify-center flex-shrink-0">
                    <MapPin size={18} className="text-[#44B6CE]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-gray-900">
                      {selectedSavedLocation.location.split(" - ")[0]}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5 truncate">
                      {selectedSavedLocation.location.split(" - ")[1] ||
                        selectedSavedLocation.location}
                    </p>
                  </div>
                </div>
              ) : null}
              <FieldError field="location" />

              <div className="pt-2">
                <p className="text-sm font-semibold text-gray-700 mb-2">
                  Pickup Time
                </p>
                <TimeSlotPicker
                  type="pickup"
                  value={pickupSlot}
                  onChange={setPickupSlot}
                />
                <FieldError field="slot" />
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm font-semibold text-gray-700">
                Locker Location
              </p>
              <div className="relative">
                <select
                  value={selectedLockerId}
                  disabled={lockerLoading}
                  onChange={(e) => setSelectedLockerId(e.target.value)}
                  className="w-full appearance-none bg-white border border-gray-100 shadow-sm rounded-2xl px-4 py-4 text-sm outline-none focus:border-[#44B6CE]"
                >
                  <option value="" disabled>
                    {lockerLoading ? "Loading..." : "Select locker location"}
                  </option>
                  {lockerLocations
                    .filter((l: LockerLocation) => l.is_active)
                    .map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.location}
                      </option>
                    ))}
                </select>
                <ChevronRight
                  size={18}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 rotate-90 pointer-events-none"
                />
              </div>
              <FieldError field="locker" />
            </div>
          )}

          <div>
            <p className="text-sm font-semibold text-gray-700 mb-2">
              Notes / Instructions{" "}
              <span className="font-normal text-gray-400">(optional)</span>
            </p>
            <div className="flex flex-wrap gap-2 mb-2">
              {NOTE_SUGGESTIONS.map((text) => (
                <button
                  key={text}
                  type="button"
                  onClick={() => addNoteSuggestion(text)}
                  className="rounded-full bg-white border border-gray-100 px-3 py-1.5 text-xs font-semibold text-gray-600 active:bg-gray-50"
                >
                  + {text}
                </button>
              ))}
            </div>
            <textarea
              placeholder="e.g. Press only for the shirts, the red dress is delicate"
              rows={3}
              value={note}
              maxLength={500}
              onChange={(e) => setNote(e.target.value)}
              className="w-full bg-white rounded-2xl px-4 py-3 text-sm border border-gray-100 shadow-sm outline-none focus:border-[#44B6CE] resize-none"
            />
          </div>

          <div className="bg-[#44B6CE]/5 border border-[#44B6CE]/20 rounded-2xl px-4 py-3">
            <p className="text-xs text-gray-600 leading-relaxed">
              No payment now. Once your bag reaches our shop we'll list the
              items and send you the total with a payment link.
            </p>
          </div>

          <CustomButton
            variant="primary"
            className="w-full py-4 rounded-full font-bold mt-2"
            onClick={handleConfirmOrder}
            disabled={isOrderLoading}
          >
            {isOrderLoading ? "Placing order..." : "Confirm Order"}
          </CustomButton>
        </div>
      </div>
  );
}
